/**
 * Size limits for a media bubble in the message list — a GIF, or a lone video attachment
 * (TRE-2117). Shared so those two never drift apart visually when they sit in the same thread.
 *
 * A bubble only gets this treatment when it is the message's single attachment. Several of them
 * side by side would each be too wide to line up, which is why a multi-attachment message keeps
 * the small horizontal strip of tiles.
 */
export const CHAT_MEDIA_BUBBLE_MAX_SIZE = 240;

/** Share of a phone's width a bubble may take, so it still reads as a message, not a page. */
const VIEWPORT_WIDTH_RATIO = 0.7;

export interface ChatMediaBubbleBounds {
  maxWidth: number;
  maxHeight: number;
}

/**
 * @param viewportWidth Width to fit within. Omit on desktop, where the bubble is never the
 * constraint and the flat maximum applies.
 */
export const getChatMediaBubbleBounds = (viewportWidth?: number): ChatMediaBubbleBounds => ({
  maxWidth:
    viewportWidth && viewportWidth > 0
      ? Math.min(CHAT_MEDIA_BUBBLE_MAX_SIZE, Math.round(viewportWidth * VIEWPORT_WIDTH_RATIO))
      : CHAT_MEDIA_BUBBLE_MAX_SIZE,
  maxHeight: CHAT_MEDIA_BUBBLE_MAX_SIZE,
});

/**
 * A video attachment carries no dimensions, so its bubble is square and the poster is cropped to
 * fill — the same way the small tile already renders it.
 */
export const getChatVideoBubbleSize = (viewportWidth?: number): number =>
  getChatMediaBubbleBounds(viewportWidth).maxWidth;
